import {
  WSActions,
  WSCard,
  WSControlGroup,
  WSDivider,
  WSForm,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { TaxEstimateFilingStatus } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useState } from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  fetchTaxEstimate,
  useQueryTaxEstimate
} from "@hooks/useQueryTaxEstimate";
import { queriesTaxWithholdings } from "@query";
import { US_STATES_OPTIONS } from "../../../constants/user";
import { selectorTaxRateLabel } from "../../../shared/selectors/selectorTaxRateLabel";
import { FILING_STATUS_OPTIONS } from "../constants";
import { useModalHowItWorks } from "../modals/ModalHowItWorks";
import { useModalHowWeCalculateTaxRate } from "../modals/ModalHowWeCalculateTaxRate";

const MIN_WITHHOLDING_RATE = 1;
const MAX_WITHHOLDING_RATE = 100;

const isUsableRate = (rate?: number | null): rate is number =>
  rate !== undefined &&
  rate !== null &&
  rate >= MIN_WITHHOLDING_RATE &&
  rate <= MAX_WITHHOLDING_RATE;

const UNUSABLE_ESTIMATE_ERROR = new Error(
  "Tax estimate returned no usable effective rate"
);

export type RateSummaryProps = {
  rate?: number;
  hasAnswers: boolean;
  isFetching: boolean;
  error: unknown;
};

export const RateSummary: React.FC<RateSummaryProps> = props => {
  if (isUsableRate(props.rate)) {
    return (
      <WSInfoBox>
        Your recommended withholding rate: {selectorTaxRateLabel(props.rate)} of
        gross payments
      </WSInfoBox>
    );
  }

  if (props.isFetching) {
    return (
      <WSInfoBox>Calculating your recommended withholding rate…</WSInfoBox>
    );
  }

  if (!props.hasAnswers) {
    return (
      <WSInfoBox>
        Answer both questions above to see your recommended withholding rate.
      </WSInfoBox>
    );
  }

  return (
    <ErrorAlert
      context="TaxEstimate"
      error={props.error ?? UNUSABLE_ESTIMATE_ERROR}
    />
  );
};

type FormData = {
  filingStatus: TaxEstimateFilingStatus;
  stateOfResidence: string;
  preference: "estimated" | "custom";
  customRate?: number | null;
};

type Props = {
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  isEdit?: boolean;
};

/**
 * The rate section, shown once both tax-profile questions are answered.
 *
 * The answers gate *when* the estimate is fetched but no longer feed into it:
 * SDK 3.55.3 narrowed `TaxEstimateRequest` to `{ year }`, so the rate is
 * account-wide for the year (see the TODO(v3-blocker) in `useQueryTaxEstimate`).
 */
const RateSection: React.FC<{
  filingStatus?: TaxEstimateFilingStatus;
  stateOfResidence?: string;
}> = props => {
  const modalHowItWorks = useModalHowItWorks();

  const hasAnswers = !!props.filingStatus && !!props.stateOfResidence;

  const queryTaxEstimate = useQueryTaxEstimate(new Date().getFullYear(), {
    enabled: hasAnswers
  });
  // A percentage (e.g. 24.5). Gated on `hasAnswers` because the unanswered
  // query key is shared with the invoicing alert's backend-default estimate.
  const estimatedTaxRate = hasAnswers
    ? queryTaxEstimate.data?.effectiveTaxRate
    : undefined;
  const canUseEstimatedRate = isUsableRate(estimatedTaxRate);

  return (
    <WSCard
      header={{
        label: "Configure your tax settings",
        value: {
          action: {
            label: "How it works",
            onClick: () => {
              modalHowItWorks.open({ taxRate: estimatedTaxRate ?? 0 });
            },
            type: "button"
          }
        }
      }}
    >
      <WSList gap="L">
        <RateSummary
          rate={estimatedTaxRate}
          hasAnswers={hasAnswers}
          isFetching={queryTaxEstimate.isFetching}
          error={queryTaxEstimate.error}
        />

        <WSForm.Field
          name="preference"
          component={WSControlGroup}
          componentProps={{
            type: "radio",
            label: "Set your withholding percentage",
            required: true,
            helperText: "You can adjust this anytime as your income changes.",
            options: [
              {
                label: canUseEstimatedRate
                  ? `Use the calculated rate (${selectorTaxRateLabel(estimatedTaxRate)})`
                  : "Use the calculated rate",
                value: "estimated",
                disabled: !canUseEstimatedRate,
                helperText: canUseEstimatedRate
                  ? undefined
                  : "Available once we have calculated your rate."
              },
              {
                label: "Custom rate",
                value: "custom"
              }
            ]
          }}
        />

        <WSForm.Value name="preference">
          {preference => {
            if (preference !== "custom") {
              return null;
            }

            return (
              <WSForm.Field
                name="customRate"
                component={WSInputNumber}
                componentProps={{
                  min: 0,
                  max: MAX_WITHHOLDING_RATE,
                  maxFractionDigits: 2,
                  align: "right",
                  iconRight: "percent"
                }}
              />
            );
          }}
        </WSForm.Value>
      </WSList>
    </WSCard>
  );
};

/**
 * Tax profile and withholding rate on one screen.
 *
 * v1 split these across two screens and used the server as the handoff: the
 * profile screen wrote filing status / expected income / state of residence to
 * `member.profile.taxInfo`, and the rate screen read them back. v3 persists no
 * tax profile — `filingStatus` and `state` are only ever request inputs to
 * `POST /v3/finance/tax-estimates`, which computes without saving — so there is
 * nothing for a first screen to write. Asking both here keeps the answers in one
 * form's state, and the only thing saved is the resulting `withholdingRate`.
 */
export const FormTaxRate: React.FC<Props> = props => {
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();
  const currentTaxRate = querySettings.data?.withholdingRate;

  const modalHowWeCalculateTaxRate = useModalHowWeCalculateTaxRate();

  const [updateSettings, submitMeta] =
    queriesTaxWithholdings.updateTaxWithholdingSettings.useMutation({
      // Await the settings refetch before advancing so the next step doesn't
      // briefly render the previous rate.
      awaitDependencies: [
        queriesTaxWithholdings.getTaxWithholdingSettings.queryKeyBase
      ]
    });

  const onNext = props.onNext;

  const [estimateError, setEstimateError] = useState<unknown>(undefined);

  const submit = useCallback(
    async (data: FormData) => {
      setEstimateError(undefined);

      let newRate = data.customRate;

      if (data.preference !== "custom") {
        // Cache-first, so this is the same estimate the form was displaying —
        // resolved here rather than lifted out of RateSection so the saved rate
        // always matches what the user saw.
        try {
          const estimate = await fetchTaxEstimate(new Date().getFullYear());
          newRate = estimate.effectiveTaxRate;
        } catch (error) {
          setEstimateError(error);
          return;
        }

        if (!isUsableRate(newRate)) {
          setEstimateError(UNUSABLE_ESTIMATE_ERROR);
          return;
        }
      }

      await updateSettings(
        { taxWithholdingSettingsUpdate: { withholdingRate: newRate || 0 } },
        { onSuccess: onNext }
      );
    },
    [updateSettings, onNext]
  );

  return (
    <WSForm<FormData>
      defaultValues={{
        // v3 stores no tax profile, so there is nothing to prefill these from —
        // the member answers them on every visit.
        filingStatus: undefined,
        stateOfResidence: undefined,
        // v1 compared the saved rate against the estimate to decide whether the
        // member had picked "calculated" or "custom". The estimate now depends on
        // answers that haven't been given at mount, and v3 stores the number
        // without the preference — so an existing rate preselects Custom holding
        // that value. That keeps a deliberate custom rate intact (never silently
        // replaced); the cost is that a previously-accepted calculated rate reads
        // back as custom until the member reselects the calculated option.
        preference: currentTaxRate ? "custom" : "estimated",
        customRate: currentTaxRate
      }}
      validationSchema={Yup.object().shape({
        filingStatus: Yup.string().nullable().required("Required"),
        stateOfResidence: Yup.string().nullable().required("Required"),
        preference: Yup.string().required("Required"),
        customRate: Yup.number().when("preference", {
          is: "custom",
          then: Yup.number()
            .nullable()
            .required("Required")
            .min(MIN_WITHHOLDING_RATE, `Min value is ${MIN_WITHHOLDING_RATE}`)
            .max(MAX_WITHHOLDING_RATE, `Max value is ${MAX_WITHHOLDING_RATE}`)
        })
      })}
      onSubmit={submit}
    >
      <WSList gap="2XL">
        <WSCard
          header={
            props.isEdit
              ? "Your tax profile"
              : "Calculate your estimated tax rate"
          }
        >
          {!props.isEdit && (
            <>
              <WSText.ParagraphSm color="gray500">
                Let’s determine how much you should set aside.{" "}
                <a
                  onClick={() => {
                    modalHowWeCalculateTaxRate.open();
                  }}
                >
                  Learn more
                </a>{" "}
                about your rate calculation.
              </WSText.ParagraphSm>

              <WSDivider my="L" />
            </>
          )}

          <WSList gap="2XL">
            <WSForm.Field
              name="filingStatus"
              component={WSSelect}
              componentProps={{
                label: "What’s your filing status?",
                required: true,
                helperText:
                  "We suggest selecting the same filing status as last year unless you’ve had a major life event.",
                options: FILING_STATUS_OPTIONS
              }}
            />

            <WSForm.Field
              name="stateOfResidence"
              component={WSSelect}
              componentProps={{
                label: "What’s your State of residence?",
                required: true,
                helperText:
                  "We’ll factor both Federal and State estimates to increase the accuracy of our recommendation.",
                internalSearch: true,
                options: US_STATES_OPTIONS
              }}
            />
          </WSList>
        </WSCard>

        <WSForm.Values names={["filingStatus", "stateOfResidence"]}>
          {values => (
            <RateSection
              filingStatus={values.filingStatus}
              stateOfResidence={values.stateOfResidence}
            />
          )}
        </WSForm.Values>

        <WSText.ParagraphSm color="gray500">
          Your calculations follow IRS Form 1040-ES methodology.{" "}
          <a
            onClick={() => {
              modalHowWeCalculateTaxRate.open();
            }}
          >
            Learn more
          </a>{" "}
          about your rate calculation.
        </WSText.ParagraphSm>

        <ErrorAlert context="TaxEstimate" error={estimateError} />

        <ErrorAlert context="TaxFilling" error={submitMeta.error} />

        <WSList gap="L">
          <WSActions
            alignment="fill"
            buttons={[
              {
                type: "button",
                kind: "Tertiary",
                label: "Back",
                onClick: props.onBack,
                visible: !!props.onBack
              },
              {
                label: "Continue",
                loading: submitMeta.isLoading
              }
            ]}
          />

          {!props.isEdit && (
            <WSForm.Values names={["preference", "customRate"]}>
              {values => {
                if (values.preference === "custom" && values.customRate === 0) {
                  return (
                    <WSActions
                      alignment="center"
                      buttons={[
                        {
                          type: "button",
                          kind: "Link",
                          label: "Skip, I’ll handle taxes myself",
                          onClick: props.onSkip
                        }
                      ]}
                    />
                  );
                }

                return null;
              }}
            </WSForm.Values>
          )}
        </WSList>
      </WSList>
    </WSForm>
  );
};
